import {Popover} from '@mantine/core';
import React, {useState} from 'react';
import {HiPencil} from 'react-icons/hi';

import {
  Container,
  Input,
  OptionList,
  CheckBox,
  Active,
  Option,
  AddButtom,
} from './styles';
import IconButton from 'shared/migration-components/icon-button';
import {NestedKeys} from 'shared/types/key-manipulation';
import {ProjectionOptionProps} from '../..';
import {translatedProps} from 'shared/util/translated-props';

export interface SelectOptionProps {
  label?: string;
  value: string;
}

const isProjectionOptionProps = (
  object: any,
): object is ProjectionOptionProps => {
  return !!object.label || !!object.value;
};

type Props = {
  values?: NestedKeys[];
  onChange?: (value: any[]) => void;
  options: (NestedKeys | ProjectionOptionProps)[];
  centerValue?: boolean;
  placeholder?: string;
  translatedPropsKey: keyof typeof translatedProps;
};

const ProjectionSelect: React.FC<Props> = ({
  onChange,
  values = [],
  options,
  translatedPropsKey,
}) => {
  const [opened, setOpened] = useState(false);
  const [searchText, setSearch] = useState('');
  const [localValues, setLocalValues] = useState<string[]>(values ?? []);

  const getValue = (option: string | ProjectionOptionProps) =>
    isProjectionOptionProps(option) ? (option.value! as string) : option;

  const getNestedKey: any = (object: any, key: string) => {
    const [firstKey, ...rest] = key.split('.');

    if (rest.length === 0) return object?.[firstKey];

    return getNestedKey(object[firstKey], rest.join('.'));
  };

  const getLabel = (option: NestedKeys | ProjectionOptionProps) => {
    if (typeof option === 'string')
      return getNestedKey(translatedProps[translatedPropsKey], option);
    if (option.label) return option.label;
    return getNestedKey(translatedProps[translatedPropsKey], option.value);
  };

  const changeLocalValues = (option: string) => {
    const isValue = localValues.find((localValue) => localValue === option);

    setLocalValues(
      isValue
        ? localValues.filter((localValue) => localValue !== isValue)
        : [...localValues, option],
    );
  };

  const submitValues = () => {
    if (onChange) onChange(localValues);
    setOpened(false);
  };

  const availableOptions = options.filter(
    (option) =>
      getLabel(option)
        .toLocaleLowerCase()
        .search(searchText.toLocaleLowerCase()) >= 0,
  );

  return (
    <Popover
      closeOnClickOutside
      opened={opened}
      onClose={() => setOpened(false)}
      zIndex={1001}
      position="bottom-end"
      styles={{dropdown: {padding: 0}}}>
      <Popover.Target>
        <div>
          <IconButton
            icon={<HiPencil />}
            label="Editar colunas"
            onClick={() => setOpened(true)}
          />
        </div>
      </Popover.Target>
      <Popover.Dropdown>
        <Container full>
          <Input
            placeholder="Pesquisar"
            autoFocus
            onChange={(e) => setSearch(e.target.value)}
          />
          <OptionList>
            <Option
              {...{active: availableOptions.length === localValues.length}}
              onClick={() =>
                setLocalValues(
                  availableOptions.length === localValues.length
                    ? []
                    : availableOptions.map((option) => getValue(option)),
                )
              }>
              <CheckBox
                {...{active: availableOptions.length === localValues.length}}>
                {availableOptions.length === localValues.length && <Active />}
              </CheckBox>
              Selecionar tudo
            </Option>
            {availableOptions
              .filter(
                (option) => !isProjectionOptionProps(option) || !option.hidden,
              )
              .map((option, index) => {
                const active = localValues.find(
                  (localValue) => localValue === getValue(option),
                )
                  ? true
                  : false;

                return (
                  <Option
                    {...{active}}
                    key={index}
                    onClick={() => changeLocalValues(getValue(option))}>
                    <CheckBox {...{active}}>{active && <Active />}</CheckBox>
                    {getLabel(option)}
                  </Option>
                );
              })}
            <AddButtom onClick={submitValues}>Salvar</AddButtom>
          </OptionList>
        </Container>
      </Popover.Dropdown>
    </Popover>
  );
};

export default ProjectionSelect;
